<template>
  <Teleport :to="hasAppTarget ? '#app' : 'body'" :disabled="!hasAppTarget">
    <Transition name="bottom-sheet">
      <div v-if="modelValue" class="bottom-sheet-root">
        <div
          class="fixed inset-0 z-40 bg-black/40"
          aria-hidden="true"
          :data-testid="scrimTestId ?? `${testId}-scrim`"
          @click="close"
        ></div>
        <section
          ref="panel"
          class="bottom-sheet-panel fixed inset-x-0 bottom-0 z-50 flex flex-col rounded-t-2xl bg-white pb-0 shadow-2xl outline-none"
          :class="height === 'tall' ? 'bottom-sheet-panel--tall' : 'bottom-sheet-panel--auto'"
          role="dialog"
          aria-modal="true"
          :aria-label="ariaLabel ?? title"
          tabindex="-1"
          :data-testid="testId"
          @keydown="onPanelKeydown"
        >
          <div
            class="mx-auto mt-2 h-1 w-10 shrink-0 rounded-full bg-gray-300"
            aria-hidden="true"
          ></div>
          <header v-if="hasHeader" class="flex min-h-[48px] shrink-0 items-center gap-1 px-2 pt-1">
            <button
              v-if="canGoBack"
              type="button"
              class="flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-lg text-gray-700"
              aria-label="Voltar"
              :data-testid="`${testId}-back`"
              @click="emit('back')"
            >
              ←
            </button>
            <div class="min-w-0 flex-1 px-2 text-left">
              <h2 v-if="title" class="truncate text-base font-bold text-gray-900">{{ title }}</h2>
              <p v-if="subtitle" class="truncate text-xs text-gray-500">{{ subtitle }}</p>
            </div>
            <slot name="header-actions" />
            <button
              type="button"
              class="flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-gray-500"
              aria-label="Fechar"
              :data-testid="`${testId}-close`"
              @click="close"
            >
              ✕
            </button>
          </header>
          <div
            class="min-h-0 flex-1 overflow-y-auto overscroll-contain px-4"
            :data-testid="`${testId}-body`"
          >
            <slot />
          </div>
          <footer
            v-if="$slots.footer"
            class="shrink-0 border-t border-gray-100 bg-white px-4 pt-3 pb-[calc(0.75rem+env(safe-area-inset-bottom,0px))]"
            :data-testid="`${testId}-footer`"
          >
            <slot name="footer" />
          </footer>
        </section>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import { computed, ref, toRef, useSlots } from 'vue'
import { useOverlayA11y } from '@/composables/useOverlayA11y'

/**
 * Bottom sheet do celular (spec §4.3): puxador, fundo que fecha, Esc, foco preso e rodapé com
 * a área segura do iPhone. A pilha de subtelas (←) é do consumidor: `canGoBack` mostra a seta e
 * `back` avisa o toque.
 */
const props = withDefaults(
  defineProps<{
    modelValue: boolean
    title?: string
    subtitle?: string
    height?: 'auto' | 'tall'
    canGoBack?: boolean
    testId?: string
    /** Rótulo do diálogo quando não há título visível (ex.: DayBottomSheet). */
    ariaLabel?: string
    /** `data-testid` do fundo; padrão `${testId}-scrim`. */
    scrimTestId?: string
  }>(),
  {
    title: undefined,
    subtitle: undefined,
    height: 'auto',
    canGoBack: false,
    testId: 'bottom-sheet',
    ariaLabel: undefined,
    scrimTestId: undefined
  }
)

const emit = defineEmits<{ 'update:modelValue': [value: boolean]; back: [] }>()
const slots = useSlots()
const panel = ref<HTMLElement | null>(null)

// O Tailwind só vale dentro de #app (`important: '#app'`). Sem #app (jsdom), renderiza no lugar.
// O Vue 3.3 resolve o `to` mesmo com o Teleport desabilitado; `body` sempre existe e evita o aviso.
const hasAppTarget = typeof document !== 'undefined' && !!document.getElementById('app')

const hasHeader = computed(
  () => !!props.title || !!props.subtitle || props.canGoBack || !!slots['header-actions']
)

function close(): void {
  emit('update:modelValue', false)
}

const { onPanelKeydown } = useOverlayA11y({
  panel,
  isOpen: toRef(props, 'modelValue'),
  onEscape: close
})
</script>

<style scoped>
.bottom-sheet-panel--auto {
  max-height: 85vh;
  max-height: 85dvh;
}
.bottom-sheet-panel--tall {
  height: 92vh;
  height: 92dvh;
}
.bottom-sheet-enter-active,
.bottom-sheet-leave-active {
  transition: opacity 200ms ease;
}
.bottom-sheet-enter-active .bottom-sheet-panel,
.bottom-sheet-leave-active .bottom-sheet-panel {
  transition: transform 220ms ease;
}
.bottom-sheet-enter-from,
.bottom-sheet-leave-to {
  opacity: 0;
}
.bottom-sheet-enter-from .bottom-sheet-panel,
.bottom-sheet-leave-to .bottom-sheet-panel {
  transform: translateY(100%);
}
@media (prefers-reduced-motion: reduce) {
  .bottom-sheet-enter-active,
  .bottom-sheet-leave-active,
  .bottom-sheet-enter-active .bottom-sheet-panel,
  .bottom-sheet-leave-active .bottom-sheet-panel {
    transition: none;
  }
}
</style>
